/**
 * TRTF - automatic bundle code
 * ---------------------------------------------------------------------------
 * Keeps exactly one discount code on the cart, chosen from what is in it:
 *
 *   2 or more full tracksuits ......... BUNDLE20   (20% off the order)
 *   1 full tracksuit .................. BUNDLE     (10% off the order)
 *   a top AND a bottom ................ BUNDLE     (10% off the order)
 *   anything else ..................... no code
 *
 * HOW IT DECIDES
 *   Purely from product_type, which Shopify already returns on every cart
 *   line. No handles or product IDs are hardcoded, so a new tracksuit or a
 *   new hoodie is picked up the moment it is published with the right type.
 *   The three lists below are the entire configuration - edit those, not the
 *   logic underneath.
 *
 * WHAT IT WILL NOT TOUCH
 *   If the shopper has entered any other code - SNOW15, a VIP code, anything
 *   that is not one of ours - the script backs off and leaves the cart alone.
 *   It only ever adds or removes the two codes it owns.
 *
 * WHY /cart/update.js AND NOT /discount/CODE
 *   { discount: CODE } sets exactly one code and replaces whatever was there;
 *   { discount: '' } clears it. /discount/CODE STACKS codes onto the cart
 *   instead, leaving dead entries behind. Both were tested against this store
 *   before this file was written.
 *
 *   Shopify still has the final say: BUNDLE and BUNDLE20 are both set to
 *   "can not combine", so only one is ever actually taken off the order, and
 *   a code that fails its own conditions is simply marked inapplicable.
 */
(function () {
  'use strict';

  /* ---- configuration ---------------------------------------------------- */

  var SET_CODE  = 'BUNDLE20';   // two or more complete tracksuits
  var PAIR_CODE = 'BUNDLE';     // one complete tracksuit, or a top + a bottom

  // The only codes this script is allowed to add or remove.
  var OWNED = [PAIR_CODE, SET_CODE];

  // product_type values, lowercased. Add to these as the catalogue grows.
  var SET_TYPES    = ['tracksuit'];
  var TOP_TYPES    = ['hoodie', 'crewneck', 'zip up', 'zip-up', 'zipup'];
  var BOTTOM_TYPES = ['sweatpants', 'sweatpant', 'sweats', 'pants',
                      'shorts', 'joggers', 'jogger', 'jorts'];

  var DEBOUNCE_MS = 250;

  /* ---- state ------------------------------------------------------------ */

  var timer = null;
  var busy  = false;   // true while WE are writing, so we never watch ourselves

  function norm(s) {
    return String(s == null ? '' : s).trim().toLowerCase();
  }

  /* ---- the decision ----------------------------------------------------- */

  function decide(cart) {
    var sets = 0, tops = 0, bottoms = 0;
    var items = (cart && cart.items) || [];

    for (var i = 0; i < items.length; i++) {
      var type = norm(items[i].product_type);
      var qty  = items[i].quantity || 0;
      if (!type || qty <= 0) continue;

      // Gift and shipping-protection lines carry their own product_type and
      // fall through all three lists, so they never count toward a bundle.
      if (SET_TYPES.indexOf(type) > -1) sets += qty;
      else if (TOP_TYPES.indexOf(type) > -1) tops += qty;
      else if (BOTTOM_TYPES.indexOf(type) > -1) bottoms += qty;
    }

    if (sets >= 2) return SET_CODE;
    if (sets === 1) return PAIR_CODE;
    if (tops >= 1 && bottoms >= 1) return PAIR_CODE;
    return '';
  }

  function appliedCodes(cart) {
    return ((cart && cart.discount_codes) || [])
      .map(function (d) { return norm(d.code).toUpperCase(); })
      .filter(Boolean);
  }

  /* ---- the write -------------------------------------------------------- */

  function run() {
    if (busy) return;

    fetch('/cart.js', { credentials: 'same-origin', cache: 'no-store' })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        var applied = appliedCodes(cart);

        // Anything we do not own is the shopper's. Leave immediately.
        for (var i = 0; i < applied.length; i++) {
          if (OWNED.indexOf(applied[i]) === -1) return;
        }

        var want = decide(cart);

        // null when several of our codes are stacked from an older session,
        // which never matches want, so the next write collapses them to one.
        var have = applied.length === 1 ? applied[0]
                 : applied.length ? null
                 : '';

        if (have === want) return;   // already correct - no request, no loop

        busy = true;
        return fetch('/cart/update.js', {
          method: 'POST',
          credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ discount: want })
        })
          .then(redrawCart)
          .catch(function () {})
          .then(function () { busy = false; });
      })
      .catch(function () { busy = false; });
  }

  function schedule() {
    clearTimeout(timer);
    timer = setTimeout(run, DEBOUNCE_MS);
  }

  function redrawCart() {
    try {
      if (typeof window.upcartRefreshCart === 'function') window.upcartRefreshCart();
    } catch (e) {}
    try {
      document.dispatchEvent(new CustomEvent('trtf:bundle-updated', { bubbles: true }));
    } catch (e) {}
  }

  /* ---- watching the cart ------------------------------------------------ */

  // Every route that can change the cart, whoever makes the call - the theme,
  // UpCart, or any other app.
  var CART_WRITE = /\/cart\/(add|change|update|clear)(\.js)?(\?|$)/i;

  if (window.fetch) {
    var originalFetch = window.fetch;
    window.fetch = function (input) {
      var url = typeof input === 'string' ? input : (input && input.url) || '';
      var promise = originalFetch.apply(this, arguments);
      if (!busy && CART_WRITE.test(url)) {
        promise.then(schedule, function () {});
      }
      return promise;
    };
  }

  var originalOpen = XMLHttpRequest.prototype.open;
  XMLHttpRequest.prototype.open = function (method, url) {
    this.__trtfCartWrite = CART_WRITE.test(String(url || ''));
    return originalOpen.apply(this, arguments);
  };

  var originalSend = XMLHttpRequest.prototype.send;
  XMLHttpRequest.prototype.send = function () {
    if (this.__trtfCartWrite) {
      this.addEventListener('loadend', function () { if (!busy) schedule(); });
    }
    return originalSend.apply(this, arguments);
  };

  // UpCart exposes these as empty hooks it calls itself. Wrap, never replace,
  // so anything already registered keeps working.
  ['upcartOnCartLoaded', 'upcartOnCartUpdated',
   'upcartOnAddToCart', 'upcartOnItemRemoved'].forEach(function (hook) {
    var previous = window[hook];
    window[hook] = function () {
      try {
        if (typeof previous === 'function') previous.apply(this, arguments);
      } catch (e) {}
      schedule();
    };
  });

  /* ---- start ------------------------------------------------------------ */

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', schedule);
  } else {
    schedule();
  }

  // Returning to the page from the back/forward cache does not re-run the
  // script, but the cart may have changed in another tab.
  window.addEventListener('pageshow', schedule);
})();
